import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import Header from './Header';
import QuestionCard from './QuestionCard';
import axios from 'axios';

const QuestionsPage = () => {
  const [questions, setQuestions] = useState([]);
  const [userResponses, setUserResponses] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const userEmail = localStorage.getItem('currentUser');

  useEffect(() => {
    if (!userEmail) {
      console.error("User not authenticated");
      window.location.href = '/login';
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch all questions
        const questionsResponse = await axios.post('/api/xapi', {
          action: 'getQuestions'
        });
        
        // Fetch user's previous responses
        const responsesResponse = await axios.post('/api/xapi', {
          action: 'getUserQuestionResponses',
          email: userEmail
        });
        
        setQuestions(questionsResponse.data.questions || []);
        setUserResponses(responsesResponse.data.responses || []);
        setError(null);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load questions. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [userEmail]);

  const handleAnswer = async (questionId, response) => {
    try {
      await axios.post('/api/xapi', {
        action: 'saveUserQuestionResponse',
        email: userEmail,
        questionId,
        userResponse: response
      });
      
      // Update local state to reflect the new response
      const updatedResponses = [...userResponses];
      const existingIndex = updatedResponses.findIndex(r => r.questionId === questionId);
      
      if (existingIndex >= 0) {
        updatedResponses[existingIndex] = {
          ...updatedResponses[existingIndex],
          response,
          updatedAt: new Date()
        };
      } else {
        updatedResponses.push({
          questionId,
          response,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      
      setUserResponses(updatedResponses);
      setSuccess('Your answer has been saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving response:', error);
      setError('Failed to save your answer. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Find user's response for a specific question
  const findUserResponse = (questionId) => {
    const response = userResponses.find(r => r.questionId === questionId);
    return response ? response.response : null;
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card mb-8"
          >
            <h2 className="text-3xl font-bold text-brandDark font-heading mb-2">Questions</h2>
            <p className="text-gray-600">
              Please answer the following questions to help us better understand your preferences and experience.
            </p>
          </motion.div>
          
          {error && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-red-50 border-l-4 border-danger p-4 rounded-lg mb-6 flex items-center"
            >
              <FiAlertCircle className="text-danger mr-3 text-xl" />
              <p className="text-danger">{error}</p>
            </motion.div>
          )}
          
          {success && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-green-50 border-l-4 border-success p-4 rounded-lg mb-6 flex items-center"
            >
              <FiCheckCircle className="text-success mr-3 text-xl" />
              <p className="text-success">{success}</p>
            </motion.div>
          )}
          
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="spinner mr-3"></div>
              <p className="text-gray-500">Loading questions...</p>
            </div>
          ) : questions.length === 0 ? (
            <div className="card text-center py-8">
              <p className="text-gray-600">No questions available at this time.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {questions.map(question => (
                <QuestionCard 
                  key={question._id}
                  question={question}
                  onAnswer={handleAnswer}
                  userResponse={findUserResponse(question._id)}
                  isCompleted={!!findUserResponse(question._id)}
                />
              ))}
            </div>
          )}
          
          <div className="flex justify-center mt-8">
            <motion.a 
              href="/onboarding"
              className="btn btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Onboarding
            </motion.a>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestionsPage; 